// Crew's sidebar (signature piece): shadcn Sidebar restyled to the Paper "Family" nav —
// 232px, 34px rows, 7px radius, white + hairline for the active row, uppercase group labels,
// a fixed 28px count lane, square agent avatars and a tinted "Computer" well.
import { Lock, Plus } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import {
  AgentAvatar,
  PersonAvatar,
  type AgentTint,
  type PersonTint,
} from "@crew/ui/components/crew-avatar";
import { Link } from "@crew/ui/components/link";
import type { IconComponent } from "@crew/ui/components/nav-icons";
import {
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupLabel,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@crew/ui/components/sidebar";
import { CountBadge, StatusDot, type StatusTone } from "@crew/ui/components/status";
import { toneText, type Tone } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";

/** Pass to `SidebarProvider style` for Paper's 232px sidebar. */
export const crewSidebarStyle = { "--sidebar-width": "232px" } as CSSProperties;

const rowClass =
  "h-[34px] gap-2.5 rounded-nav px-2 text-nav font-medium text-copy hover:bg-foreground/[0.04] hover:text-foreground data-active:bg-sidebar-accent data-active:font-medium data-active:text-foreground data-active:shadow-nav [&_svg]:text-muted-foreground data-active:[&_svg]:text-foreground";

/** Workspace name + monogram. */
export function CrewWorkspaceHeader({
  name,
  detail,
  href,
}: {
  name: string;
  detail: string;
  /** Makes the header a link (the shell's: Today). */
  href?: string;
}) {
  const body = (
    <>
      <span
        aria-hidden
        className="flex size-7 shrink-0 items-center justify-center rounded-nav bg-ink font-display text-lg text-background"
      >
        {name.replace(/^The /, "").charAt(0)}
      </span>
      <div className="flex min-w-0 flex-col">
        <span className="text-nav font-semibold text-foreground">{name}</span>
        <span className="text-xs text-muted-foreground">{detail}</span>
      </div>
    </>
  );
  if (!href) {
    return (
      <div data-slot="crew-workspace-header" className="flex items-center gap-2.5 px-4 pt-3 pb-1">
        {body}
      </div>
    );
  }
  return (
    <div data-slot="crew-workspace-header" className="px-2 pt-1">
      <Link
        href={href}
        className="flex items-center gap-2.5 rounded-nav px-2 py-2 outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-sidebar-ring"
      >
        {body}
      </Link>
    </div>
  );
}

/** Group with an uppercase label and optional "+" action. */
export function CrewNavGroup({
  label,
  onAdd,
  children,
  className,
}: {
  label?: string;
  onAdd?: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <SidebarGroup className={cn("px-4 py-1.5", className)}>
      {label ? (
        <SidebarGroupLabel className="h-7 text-xs font-semibold tracking-label text-muted-foreground uppercase">
          {label}
        </SidebarGroupLabel>
      ) : null}
      {label && onAdd ? (
        <SidebarGroupAction
          title={`Add to ${label.toLowerCase()}`}
          onClick={onAdd}
          className="top-2.5"
        >
          <Plus className="text-muted-foreground" />
        </SidebarGroupAction>
      ) : null}
      <ul data-slot="sidebar-menu" className="flex w-full min-w-0 flex-col gap-0.5">
        {children}
      </ul>
    </SidebarGroup>
  );
}

/** Nav row: icon, label, and a 28px lane for a quiet count or a copper badge. */
export function CrewNavItem({
  href,
  icon: Icon,
  label,
  active = false,
  count,
  badge,
  compact = false,
  dot = false,
  locked = false,
}: {
  href: string;
  icon: IconComponent;
  label: string;
  active?: boolean;
  count?: number;
  badge?: number;
  /** 32px rows without a count lane (footer links). */
  compact?: boolean;
  /** A copper dot after the label: something new here (What's new). */
  dot?: boolean;
  /** A small lock after the label: a private Area (slice 6g §8.2). */
  locked?: boolean;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={active}
        render={<Link href={href} aria-current={active ? "page" : undefined} />}
        className={cn(rowClass, compact && "h-8")}
      >
        <Icon />
        {locked ? (
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className="truncate">{label}</span>
            <Lock
              aria-label="Private"
              data-area-lock
              className="size-3! shrink-0 text-muted-foreground"
              strokeWidth={2.4}
            />
          </span>
        ) : (
          <span className="flex-1 truncate">{label}</span>
        )}
        {dot ? <NewDot /> : null}
        {compact ? null : (
          <span className="flex w-7 shrink-0 justify-end">
            {badge ? (
              <CountBadge>{badge}</CountBadge>
            ) : count !== undefined ? (
              <span className="text-xs font-normal text-muted-foreground tabular">{count}</span>
            ) : null}
          </span>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/** The copper dot for something not seen yet. */
export function NewDot({ className }: { className?: string }) {
  return (
    <span
      data-whats-new-dot
      aria-label="New updates"
      className={cn("size-1.5 shrink-0 rounded-full bg-copper", className)}
    />
  );
}

/** A crew member: square avatar (a presence dot while the computer is awake), name, one-line status. */
export function CrewAgentItem({
  href,
  name,
  glyph,
  tint,
  status,
  statusTone = "muted",
  active = false,
  awake = false,
}: {
  href: string;
  name: string;
  glyph: string;
  tint: AgentTint;
  status: string;
  statusTone?: Tone;
  active?: boolean;
  awake?: boolean;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={active}
        render={<Link href={href} aria-current={active ? "page" : undefined} />}
        className={cn(rowClass, "h-10")}
      >
        <AgentAvatar glyph={glyph} tint={tint} awake={awake} />
        <span className="flex min-w-0 flex-1 flex-col leading-4">
          <span className="truncate text-meta font-medium text-foreground">
            {name}
            {awake ? <span className="sr-only"> (computer awake)</span> : null}
          </span>
          <span className={cn("truncate text-xs font-normal", toneText[statusTone])}>{status}</span>
        </span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/** "Computer asleep / working" well. */
export function CrewComputerStatus({
  tone,
  title,
  detail,
}: {
  tone: StatusTone;
  title: string;
  detail: string;
}) {
  return (
    <div
      data-slot="crew-computer-status"
      className="mx-4 mb-3 flex flex-col gap-1 rounded-well bg-muted p-3"
    >
      <div className="flex items-center gap-2">
        <StatusDot tone={tone} />
        <span className="text-meta font-semibold text-foreground">{title}</span>
      </div>
      <p className="text-xs leading-[17px] text-muted-foreground">{detail}</p>
    </div>
  );
}

/** Signed-in person at the bottom. */
export function CrewUserCard({
  name,
  role,
  initials,
  tint = "sand",
}: {
  name: string;
  role: string;
  initials: string;
  tint?: PersonTint;
}) {
  return (
    <div
      data-slot="crew-user-card"
      className="mx-4 mt-3 flex items-center gap-2.5 border-t border-sidebar-border px-2 pt-2 pb-1"
    >
      <PersonAvatar initials={initials} tint={tint} />
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-meta font-semibold text-foreground">{name}</span>
        <span className="text-xs text-muted-foreground">{role}</span>
      </div>
    </div>
  );
}
